import {useParams, useNavigate} from "react-router-dom";
import {useAuth} from "@clerk/clerk-react";
import {useCollection, useDoc} from "../hooks/useFirestore";
import {Item, Image as ItemImage} from "../types/item";
import {Collection} from "../types/collection";
import {
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    IconButton,
    Box,
    CircularProgress,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActionArea,
    ToggleButtonGroup,
    ToggleButton,
    Paper,
    Chip,
    Stack,
    Autocomplete,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    InputAdornment
} from "@mui/material";
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Image as ImageIcon,
    CloudUpload as CloudUploadIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    PhotoCamera as PhotoCameraIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {addDoc, collection, doc, deleteDoc} from 'firebase/firestore';
import {db, storage} from '../firebase';
import React, {useState, useRef, useEffect, useMemo} from "react";
import {updateDoc} from "@firebase/firestore";
import {ref, uploadBytes, getDownloadURL} from 'firebase/storage';

export default function CollectionItemsLayout() {
    const {cid} = useParams();
    const navigate = useNavigate();
    const {userId} = useAuth();
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
    const [newItemName, setNewItemName] = useState('');
    const [newItemDescription, setNewItemDescription] = useState('');
    const [newItemTags, setNewItemTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [images, setImages] = useState<Array<{file: File, preview: string}>>([]);
    const [imageUrl, setImageUrl] = useState<string | null>(null); // For preview only
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'thumbnails'>(() => {
        const savedViewMode = localStorage.getItem('itemsViewMode');
        return (savedViewMode === 'thumbnails' ? 'thumbnails' : 'list') as 'list' | 'thumbnails';
    });
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const {
        snapshot: collectionData,
        isLoading: isLoadingCollection
    } = useDoc<Collection>(`users/${userId}/collections/${cid}`)
    
    // Save view mode preference to localStorage
    useEffect(() => {
        localStorage.setItem('itemsViewMode', viewMode);
    }, [viewMode]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setNewItemName('');
        setNewItemDescription('');
        setNewItemTags([]);
        setTagInput('');
        setImages([]);
        setImageUrl(null);
    };
    
    const handleViewModeChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: 'list' | 'thumbnails' | null,
    ) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };
    
    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            const preview = URL.createObjectURL(selectedFile);
            
            // Add to images array
            setImages([...images, { file: selectedFile, preview }]);
            
            // Set the first image preview for thumbnail display
            if (images.length === 0 && !imageUrl) {
                setImageUrl(preview);
            }
        }
    };
    
    const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const capturedImage = e.target.files[0];
            const preview = URL.createObjectURL(capturedImage);
            
            // Add to images array
            setImages([...images, { file: capturedImage, preview }]);
            
            // Set the first image preview for thumbnail display
            if (images.length === 0 && !imageUrl) {
                setImageUrl(preview);
            }
        }
    };
    
    const handleRemoveImage = (index: number) => {
        const newImages = images.filter((_, i) => i !== index);
        setImages(newImages);
        
        // Update preview if we removed the first image
        if (index === 0 && newImages.length > 0) {
            setImageUrl(newImages[0].preview);
        } else if (newImages.length === 0) {
            setImageUrl(null);
        }
    };

    const uploadImage = async (file: File) => {
        if (!file || !userId || !cid) return null;
        
        try {
            setUploading(true);
            const storageRef = ref(storage, `users/${userId}/collections/${cid}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(snapshot.ref);
            return downloadUrl;
        } catch (error) {
            console.error("Error uploading image: ", error);
            return null;
        } finally {
            setUploading(false);
        }
    };

    const createItem = async () => {
        if (newItemName.trim() && cid) {
            try {
                setUploading(true);
                
                // Create an array to store all uploaded image URLs
                const uploadedImages: ItemImage[] = [];
                
                // Upload all images if they exist
                for (const img of images) {
                    const downloadUrl = await uploadImage(img.file);
                    if (downloadUrl) {
                        uploadedImages.push({
                            url: downloadUrl,
                            createdAt: new Date() as any // Firebase will convert to Timestamp
                        });
                    }
                }
                
                // Use the first image URL as the main imageUrl for backward compatibility
                const mainImageUrl = uploadedImages.length > 0 ? uploadedImages[0].url : null;
                
                // Update collection item count
                await updateDoc(doc(db, `users/${userId}/collections/${cid}`), "itemCount", (collectionData?.itemCount ?? 0) + 1)
                
                // Create item document with image URLs if available
                await addDoc(collection(db, `users/${userId}/collections/${cid}/items`), {
                    name: newItemName.trim(),
                    description: newItemDescription.trim(),
                    imageUrl: mainImageUrl, // For backward compatibility
                    images: uploadedImages,
                    tags: newItemTags,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    collectionId: cid
                });
                
                handleClose();
            } catch (error) {
                console.error("Error creating item: ", error);
            } finally {
                setUploading(false);
            }
        }
    };

    const {snapshot: allItems, error, isLoading} = useCollection<Item>(`users/${userId}/collections/${cid}/items`);
    
    // Handle item deletion
    const handleDeleteItem = async () => {
        if (!itemToDelete || !cid || !userId) return;
        
        try {
            setDeleting(true);
            
            // Delete the item document
            await deleteDoc(doc(db, `users/${userId}/collections/${cid}/items/${itemToDelete.id}`));
            
            // Update collection item count
            await updateDoc(doc(db, `users/${userId}/collections/${cid}`), {
                itemCount: Math.max((collectionData?.itemCount ?? 1) - 1, 0)
            });
            
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error("Error deleting item: ", error);
        } finally {
            setDeleting(false);
        }
    };
    
    // Extract all unique tags from items for the filter
    useEffect(() => {
        if (allItems && allItems.length > 0) {
            const tagSet = new Set<string>();
            allItems.forEach(item => {
                if (item.tags && item.tags.length > 0) {
                    item.tags.forEach(tag => tagSet.add(tag));
                }
            });
            setAvailableTags(Array.from(tagSet).sort());
        } else {
            setAvailableTags([]);
        }
    }, [allItems]);
    
    // Filter items by selected tags and search query
    const filteredItems = useMemo(() => {
        if (!allItems) return [];

        let items = allItems;

        if (selectedTags.length > 0) {
            items = items.filter(item => {
                if (!item.tags) return false;
                return selectedTags.every(tag => item.tags!.includes(tag));
            });
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            items = items.filter(item => {
                const nameMatch = item.name.toLowerCase().includes(query);
                const descMatch = item.description?.toLowerCase().includes(query);
                const tagMatch = item.tags?.some(tag => tag.toLowerCase().includes(query));
                return nameMatch || descMatch || tagMatch;
            });
        }

        return items;
    }, [allItems, selectedTags, searchQuery]);

    return (
        <div>
            <Box sx={{
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: 1.5, sm: 2 },
                flexWrap: 'wrap',
                px: { xs: 0.5, sm: 0 }
            }}>
                <IconButton onClick={() => navigate('/h/dashboard')} sx={{mr: 1}}>
                    <ArrowBackIcon/>
                </IconButton>
                <Typography 
                    variant="h4" 
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                        wordBreak: 'break-word'
                    }}
                >
                    {isLoadingCollection ? 'Loading...' : collectionData?.name || 'Collection Items'}
                </Typography>
            </Box>

            <Box 
                sx={{
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 0 },
                    marginBottom: { xs: '12px', sm: '20px' },
                    px: { xs: 0.5, sm: 0 }
                }}
            >
                <Box 
                    sx={{
                        display: 'flex', 
                        width: '100%',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2
                    }}
                >
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 2,
                        flex: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' } }}>
                            <Typography variant="h6" sx={{mr: 2}}>Items</Typography>
                            <Paper elevation={0} sx={{display: 'flex', alignItems: 'center'}}>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={handleViewModeChange}
                                    aria-label="view mode"
                                    size="small"
                                >
                                    <ToggleButton value="list" aria-label="list view">
                                        <ViewListIcon />
                                    </ToggleButton>
                                    <ToggleButton value="thumbnails" aria-label="thumbnails view">
                                        <ViewModuleIcon />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Paper>
                        </Box>

                        <TextField
                            size="small"
                            placeholder="Search items..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ width: { xs: '100%', sm: 250 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {availableTags.length > 0 && (
                            <Box 
                                sx={{ 
                                    flex: 1,
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                        Filter:
                                    </Typography>
                                    {selectedTags.length > 0 ? (
                                        <>
                                            {selectedTags.map((tag, index) => (
                                                <Chip 
                                                    key={index}
                                                    label={tag}
                                                    color="primary"
                                                    onDelete={() => {
                                                        setSelectedTags(selectedTags.filter(t => t !== tag));
                                                    }}
                                                    size="small"
                                                />
                                            ))}
                                            {selectedTags.length > 0 && (
                                                <Chip
                                                    label="Clear All"
                                                    size="small"
                                                    color="default"
                                                    onClick={() => setSelectedTags([])}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {availableTags.map((tag, index) => (
                                                <Chip 
                                                    key={index}
                                                    label={tag}
                                                    variant="outlined"
                                                    onClick={() => setSelectedTags([...selectedTags, tag])}
                                                    size="small"
                                                    color="default"
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClickOpen}
                        startIcon={<AddIcon/>}
                        sx={{ 
                            width: { xs: '100%', sm: 'auto' },
                            alignSelf: { xs: 'stretch', sm: 'flex-start' }
                        }}
                    >
                        Add Item
                    </Button>
                </Box>
                <Dialog 
                    open={open} 
                    onClose={handleClose}
                    fullWidth
                    maxWidth="sm"
                    sx={{
                        '& .MuiDialog-paper': {
                            margin: { xs: '16px', sm: '32px' },
                            width: { xs: 'calc(100% - 32px)', sm: '500px' },
                            maxWidth: { xs: 'calc(100% - 32px)', sm: '500px' },
                        }
                    }}
                >
                    <DialogTitle>Add New Item</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Item Name"
                            type="text"
                            fullWidth
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                        />
                        <TextField
                            margin="dense"
                            label="Description"
                            type="text"
                            fullWidth
                            multiline
                            rows={4}
                            value={newItemDescription}
                            onChange={(e) => setNewItemDescription(e.target.value)}
                        />

                        {/* Tags input */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Tags
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                {newItemTags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => {
                                            setNewItemTags(newItemTags.filter((_, i) => i !== index));
                                        }}
                                        size="small"
                                    />
                                ))}
                            </Stack>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Autocomplete
                                    size="small"
                                    freeSolo
                                    fullWidth
                                    value={tagInput}
                                    inputValue={tagInput}
                                    onInputChange={(event, newInputValue) => {
                                        setTagInput(newInputValue);
                                    }}
                                    onChange={(event, newValue) => {
                                        if (newValue && !newItemTags.includes(newValue)) {
                                            setNewItemTags([...newItemTags, newValue]);
                                            setTagInput('');
                                        }
                                    }}
                                    options={availableTags.filter(tag => !newItemTags.includes(tag))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Add a tag"
                                            size="small"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && tagInput.trim()) {
                                                    e.preventDefault();
                                                    if (!newItemTags.includes(tagInput.trim())) {
                                                        setNewItemTags([...newItemTags, tagInput.trim()]);
                                                    }
                                                    setTagInput('');
                                                }
                                            }}
                                        />
                                    )}
                                />
                                <Button 
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        if (tagInput.trim() && !newItemTags.includes(tagInput.trim())) {
                                            setNewItemTags([...newItemTags, tagInput.trim()]);
                                            setTagInput('');
                                        }
                                    }}
                                    disabled={!tagInput.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Box>
                        
                        {/* Hidden file inputs */}
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={handleImageSelect}
                        />
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            style={{ display: 'none' }}
                            ref={cameraInputRef}
                            onChange={handleCameraCapture}
                        />
                        
                        {/* Image upload buttons */}
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Images
                            </Typography>
                            
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2, 
                                mb: 2,
                                width: '100%'
                            }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    fullWidth
                                    sx={{ height: { xs: '48px', sm: 'auto' } }}
                                >
                                    Upload Image
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => cameraInputRef.current?.click()}
                                    disabled={uploading}
                                    fullWidth
                                    sx={{ height: { xs: '48px', sm: 'auto' } }}
                                >
                                    Take Photo
                                </Button>
                            </Box>
                            
                            {/* Image previews */}
                            {images.length > 0 && (
                                <Box sx={{ width: '100%', mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {images.length} image{images.length !== 1 ? 's' : ''} selected
                                    </Typography>
                                    <ImageList 
                                        sx={{ 
                                            width: '100%',
                                            maxHeight: 300,
                                            mb: 2
                                        }}
                                        cols={3}
                                        rowHeight={100}
                                        gap={8}
                                    >
                                        {images.map((image, index) => (
                                            <ImageListItem key={index}>
                                                <img
                                                    src={image.preview}
                                                    alt={`Item ${index + 1}`}
                                                    loading="lazy"
                                                    style={{ objectFit: 'contain', height: '100%' }}
                                                />
                                                <ImageListItemBar
                                                    sx={{
                                                        background: 'rgba(0, 0, 0, 0.4)'
                                                    }}
                                                    position="top"
                                                    actionIcon={
                                                        <IconButton
                                                            sx={{ color: 'white' }}
                                                            onClick={() => handleRemoveImage(index)}
                                                            disabled={uploading}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    }
                                                />
                                            </ImageListItem>
                                        ))}
                                    </ImageList>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
                        <Button 
                            onClick={createItem} 
                            color="primary"
                            disabled={!newItemName.trim() || uploading}
                        >
                            {uploading ? <CircularProgress size={24} /> : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        if (!deleting) {
                            setDeleteDialogOpen(false);
                            setItemToDelete(null);
                        }
                    }}
                >
                    <DialogTitle>Delete Item</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                setItemToDelete(null);
                            }} 
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteItem} 
                            color="error" 
                            disabled={deleting}
                        >
                            {deleting ? <CircularProgress size={24} /> : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            {isLoading ? (
                <div>Loading...</div>
            ) : error ? (
                <div>Error: {error.message}</div>
            ) : allItems.length === 0 ? (
                <div>No items found in this collection</div>
            ) : filteredItems.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {searchQuery.trim()
                            ? `No items match "${searchQuery.trim()}".`
                            : 'No items match the selected tags.'}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setSelectedTags([]); setSearchQuery(''); }}
                        sx={{ mt: 2 }}
                    >
                        Clear Filters
                    </Button>
                </Box>
            ) : viewMode === 'list' ? (
                <List 
                    sx={{
                        width: '100%', 
                        bgcolor: 'background.paper',
                        borderRadius: { xs: '4px', sm: '8px' },
                        overflow: 'hidden',
                        mx: { xs: -0.5, sm: 0 }
                    }}
                >
                    {filteredItems.map(item => (
                        <ListItem 
                            key={item.id} 
                            disablePadding
                            secondaryAction={
                                <Box sx={{ display: 'flex' }}>
                                    <IconButton 
                                        edge="end" 
                                        aria-label="edit"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/h/collection/${cid}/item/${item.id}/edit`);
                                        }}
                                        size="small"
                                        sx={{ 
                                            mr: 0.5,
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        edge="end" 
                                        aria-label="delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setItemToDelete(item);
                                            setDeleteDialogOpen(true);
                                        }}
                                        size="small"
                                        sx={{ 
                                            color: 'error.main',
                                            '&:hover': {
                                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            }
                            sx={{ 
                                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                                '&:last-child': {
                                    borderBottom: 'none'
                                },
                                pr: 8 // Make room for action buttons
                            }}
                        >
                            <ListItemButton onClick={() => navigate(`/h/collection/${cid}/item/${item.id}`)}>
                                <ListItemAvatar>
                                    <Avatar 
                                        src={item.imageUrl || undefined}
                                        variant="rounded"
                                        sx={{ 
                                            width: { xs: 40, sm: 48 },
                                            height: { xs: 40, sm: 48 },
                                            borderRadius: '4px',
                                            bgcolor: 'rgba(0, 0, 0, 0.03)'
                                        }}
                                    >
                                        {!item.imageUrl && <ImageIcon sx={{ fontSize: { xs: 20, sm: 24 } }}/>}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontWeight: 500,
                                                fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}
                                        >
                                            {item.name}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {item.description || 'No description'}
                                            </Typography>
                                            {item.tags && item.tags.length > 0 && (
                                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {item.tags.slice(0, 3).map((tag, idx) => (
                                                        <Chip 
                                                            key={idx} 
                                                            label={tag} 
                                                            size="small" 
                                                            variant="outlined"
                                                            sx={{ 
                                                                height: '20px',
                                                                '& .MuiChip-label': {
                                                                    px: 1,
                                                                    fontSize: '0.625rem'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {item.tags.length > 3 && (
                                                        <Chip 
                                                            label={`+${item.tags.length - 3}`} 
                                                            size="small"
                                                            sx={{ 
                                                                height: '20px',
                                                                '& .MuiChip-label': {
                                                                    px: 1,
                                                                    fontSize: '0.625rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mx: { xs: -0.5, sm: 0 } }}>
                    {filteredItems.map(item => (
                        <Grid item xs={6} sm={6} md={4} lg={3} key={item.id}>
                            <Card sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                position: 'relative'
                            }}>
                                <Box 
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 4, 
                                        right: 4, 
                                        zIndex: 1,
                                        display: 'flex',
                                        gap: 0.5,
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        borderRadius: '4px',
                                        padding: '2px'
                                    }}
                                >
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/h/collection/${cid}/item/${item.id}/edit`);
                                        }}
                                        sx={{ 
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setItemToDelete(item);
                                            setDeleteDialogOpen(true);
                                        }}
                                        sx={{ 
                                            color: 'error.main',
                                            '&:hover': {
                                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <CardActionArea onClick={() => navigate(`/h/collection/${cid}/item/${item.id}`)}>
                                    <CardMedia
                                        component="div"
                                        sx={{
                                            height: { xs: 120, sm: 140 },
                                            backgroundSize: 'contain',
                                            backgroundPosition: 'center',
                                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        image={item.imageUrl || undefined}
                                    >
                                        {!item.imageUrl && (
                                            <ImageIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: 'rgba(0, 0, 0, 0.3)' }} />
                                        )}
                                    </CardMedia>
                                    <CardContent sx={{ padding: { xs: 1.5, sm: 2 } }}>
                                        <Typography 
                                            gutterBottom 
                                            variant="h6" 
                                            component="div" 
                                            noWrap
                                            sx={{ 
                                                fontSize: { xs: '0.875rem', sm: '1.25rem' },
                                                mb: { xs: 0.5, sm: 1 }
                                            }}
                                        >
                                            {item.name}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                lineHeight: { xs: 1.3, sm: 1.43 },
                                                mb: (item.tags && item.tags.length > 0) ? 1 : 0
                                            }}
                                        >
                                            {item.description || 'No description'}
                                        </Typography>
                                        {item.tags && item.tags.length > 0 && (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                mt: 0.5
                                            }}>
                                                {item.tags.slice(0, 2).map((tag, idx) => (
                                                    <Chip 
                                                        key={idx} 
                                                        label={tag} 
                                                        size="small" 
                                                        variant="outlined"
                                                        sx={{ 
                                                            height: '20px',
                                                            '& .MuiChip-label': {
                                                                px: 1,
                                                                fontSize: '0.625rem'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                                {item.tags.length > 2 && (
                                                    <Chip 
                                                        label={`+${item.tags.length - 2}`} 
                                                        size="small"
                                                        sx={{ 
                                                            height: '20px',
                                                            '& .MuiChip-label': {
                                                                px: 1,
                                                                fontSize: '0.625rem'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </div>
    );
}